"use strict";

export const TASK_STATUS = Object.freeze({
    notStarted: "notStarted",
    inProgress: "inProgress",
    completed: "completed",
    failed: "failed",
});

// Keep the status keys owned by the admin actions page aligned with each service.
export const ADMIN_ACTION_TASKS = Object.freeze([
    { stateKey: "stemSeparation", taskName: "demucs" },
    { stateKey: "transcription", taskName: "transcribe", legacyTaskName: "transcription" },
    { stateKey: "nonverbalDetection", taskName: "nonverbalDetection" },
    { stateKey: "translation", taskName: "translation" },
    { stateKey: "audioChopping", taskName: "audioChopping" },
    { stateKey: "voiceTraining", taskName: "voiceTraining" },
    { stateKey: "voiceGeneration", taskName: "voiceGeneration" },
    { stateKey: "lipSync", taskName: "lipSync" },
    { stateKey: "merging", taskName: "merging" },
]);

const VALID_TASK_STATUSES = new Set(Object.values(TASK_STATUS));

export const normalizeTaskStatus = (status) => {
    // Support the status value written by older service revisions.
    if (status === "processing") {
        return TASK_STATUS.inProgress;
    }

    return VALID_TASK_STATUSES.has(status) ? status : TASK_STATUS.notStarted;
};

const isCount = (value) => Number.isInteger(value) && value >= 0;

const STEP_STATUSES = new Set(["pending", "active", "done", "failed"]);

// Transcription reports an ordered list of steps instead of item counts.
const normalizeStepProgress = (progress) => {
    const steps = Array.isArray(progress.steps)
        ? progress.steps.filter(
            (step) => typeof step?.key === "string" && STEP_STATUSES.has(step.status)
        )
        : [];
    if (steps.length === 0) {
        return null;
    }
    return {
        kind: "steps",
        mode: typeof progress.mode === "string" ? progress.mode : null,
        steps,
        dialogues: isCount(progress.dialogues) ? progress.dialogues : null,
        updatedAt: progress.updatedAt || null,
    };
};

// Services that process items one by one report { total, succeeded, failed }.
export const normalizeTaskProgress = (progress) => {
    if (progress?.kind === "steps") {
        return normalizeStepProgress(progress);
    }
    if (
        !progress ||
        !isCount(progress.total) ||
        !isCount(progress.succeeded) ||
        !isCount(progress.failed)
    ) {
        return null;
    }

    return {
        total: progress.total,
        succeeded: progress.succeeded,
        failed: progress.failed,
        // Optional outcomes reported by some services (e.g. voice training).
        skipped: isCount(progress.skipped) ? progress.skipped : 0,
        // Why voices with samples did not train; only voice training reports these.
        notEnoughSamples: isCount(progress.notEnoughSamples) ? progress.notEnoughSamples : 0,
        restricted: isCount(progress.restricted) ? progress.restricted : 0,
        // Items done before this run; voice training reports it as alreadyTrained.
        alreadyDone: [progress.alreadyDone, progress.alreadyTrained].find(isCount) ?? 0,
        charactersWithoutVoices: Array.isArray(progress.charactersWithoutVoices)
            ? progress.charactersWithoutVoices.filter(
                (name) => typeof name === "string" && name.trim()
            )
            : [],
        mode: typeof progress.mode === "string" ? progress.mode : null,
        startedAt: progress.startedAt || null,
        byLanguage: Array.isArray(progress.byLanguage)
            ? progress.byLanguage.filter(
                (entry) => typeof entry?.language === "string" && isCount(entry.total)
            )
            : [],
        updatedAt: progress.updatedAt || null,
    };
};

const toText = (value) =>
    typeof value === "string" && value.trim() ? value.trim() : null;

// Services record why a task failed as
// { title, detail, service, code, reference, failedAt }: detail says in plain
// words what went wrong and what to do; reference finds the raw error in logs.
export const normalizeTaskError = (error) => {
    const title = toText(error?.title);
    const detail = toText(error?.detail);
    if (!title && !detail) {
        return null;
    }

    return {
        title: title || "Task failed",
        detail,
        service: toText(error.service),
        code: typeof error.code === "number" || typeof error.code === "string"
            ? error.code
            : null,
        reference: toText(error.reference),
        failedAt: toText(error.failedAt),
    };
};

// Chopping cuts the Dialogue documents transcription writes, so it can only
// start once transcription has completed and no chopping run is active.
export const getAudioChoppingBlocker = (transcriptionStatus, audioChoppingStatus) => {
    if (audioChoppingStatus === TASK_STATUS.inProgress) {
        return "Chopping is already running";
    }
    if (audioChoppingStatus === TASK_STATUS.completed) {
        return "Audio has already been chopped";
    }
    if (transcriptionStatus === TASK_STATUS.inProgress) {
        return "Wait for transcription to finish";
    }
    if (transcriptionStatus !== TASK_STATUS.completed) {
        return "Transcribe the project first";
    }
    return null;
};

const fetchConfiguredTaskStatus = async (projectId, task, requestTaskStatus) => {
    const response = await requestTaskStatus(projectId, task.taskName);
    let status = normalizeTaskStatus(response?.data?.status);
    const progress = normalizeTaskProgress(response?.data?.progress);
    const failure = normalizeTaskError(response?.data?.error);

    // Preserve status visibility for projects written before "transcribe" was canonical.
    if (status === TASK_STATUS.notStarted && task.legacyTaskName) {
        try {
            const legacyResponse = await requestTaskStatus(projectId, task.legacyTaskName);
            status = normalizeTaskStatus(legacyResponse?.data?.status);
        } catch (error) {
            // A legacy lookup failure must not discard the canonical response.
            console.warn(`Unable to fetch legacy ${task.legacyTaskName} status:`, error);
        }
    }

    return { stateKey: task.stateKey, status, progress, failure };
};

// Statuses keyed like the admin actions state, read from the taskStatuses map
// the services write on the project document, for pages that do not poll.
export const getProjectTaskStatuses = (project) => {
    const written = project?.taskStatuses || {};
    return Object.fromEntries(
        ADMIN_ACTION_TASKS.map(({ stateKey, taskName, legacyTaskName }) => {
            let status = normalizeTaskStatus(written[taskName]);
            if (status === TASK_STATUS.notStarted && legacyTaskName) {
                status = normalizeTaskStatus(written[legacyTaskName]);
            }
            return [stateKey, status];
        })
    );
};

export const loadAdminActionStatuses = async (projectId, requestTaskStatus) => {
    const results = await Promise.allSettled(
        ADMIN_ACTION_TASKS.map((task) =>
            fetchConfiguredTaskStatus(projectId, task, requestTaskStatus)
        )
    );
    const statuses = {};
    const progress = {};
    const failures = {};
    const errors = [];

    // Apply successful responses even when another service status request fails.
    results.forEach((result, index) => {
        if (result.status === "fulfilled") {
            statuses[result.value.stateKey] = result.value.status;
            progress[result.value.stateKey] = result.value.progress;
            failures[result.value.stateKey] = result.value.failure;
            return;
        }

        errors.push({
            taskName: ADMIN_ACTION_TASKS[index].taskName,
            error: result.reason,
        });
    });

    return { statuses, progress, failures, errors };
};
